import { apiClient } from '../api/client';

// 要望#205 (AI-WF254 / §18 TA-2): Web Push 購読クライアント。
// SW の登録・manifest・PWA 化は #206、購読 ON/OFF の UI 導線は #206/#211 のスコープ。
// ここは「登録済みの ServiceWorkerRegistration を受け取って購読を BE と同期する」部品のみ。

interface VapidKeyResponse {
  publicKey: string | null;
  enabled: boolean;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * push 購読を確立し BE に登録する。
 * - 通知許可が未取得なら要求する(拒否時は null)
 * - 既存購読の applicationServerKey が BE の VAPID 公開鍵と不一致(鍵ローテ後)なら
 *   購読を張り直してから登録する
 */
export async function ensurePushSubscription(
  reg: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  if (!('PushManager' in window) || !('Notification' in window)) return null;

  const { data } = await apiClient.get<VapidKeyResponse>('/api/admin/push/vapid-public-key');
  if (!data.publicKey) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const appServerKey = urlBase64ToUint8Array(data.publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const current = sub.options.applicationServerKey;
    const same =
      current &&
      new Uint8Array(current as ArrayBuffer).toString() === appServerKey.toString();
    if (!same) {
      // VAPID 鍵ローテ後: 旧鍵の購読は無効なので張り直す
      await sub.unsubscribe();
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey as BufferSource,
    });
  }

  const json = sub.toJSON();
  await apiClient.post('/api/admin/push/subscriptions', {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    userAgent: navigator.userAgent.slice(0, 255),
  });
  return sub;
}

/** push 購読を解除し BE からも削除する。 */
export async function disablePushSubscription(
  reg: ServiceWorkerRegistration,
): Promise<void> {
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await apiClient.delete('/api/admin/push/subscriptions', { data: { endpoint } });
}

/** 自分の購読一覧(購読状態の表示用)。 */
export async function listPushSubscriptions() {
  const { data } = await apiClient.get('/api/admin/push/subscriptions');
  return data as Array<{
    id: number;
    endpoint: string;
    userAgent: string | null;
    failCount: number;
    lastUsedAt: string | null;
    createdAt: string;
  }>;
}

/** テスト通知(疎通確認・QA 用)。 */
export async function sendTestPush(title?: string, body?: string) {
  const { data } = await apiClient.post('/api/admin/push/test', { title, body });
  return data as { queued: number };
}
